import React, { useEffect, useState } from 'react'
import { useRegionCountryCode } from '../../../../RegionGeo'
import { useRegionOffers } from '../../../../RegionOffers'
import { useCartStore, selectCart } from '../../../../../store/cartStore'
import { useUiStore } from '../../../../../store/uiStore'
import Text from '../../../../Text'
import '@fontsource/roboto-mono'
import { CircularProgress } from '@mui/material'
import invoiceStyles from '../InvoicePreview/styles.module.css'
import { renderInvoice } from '../InvoicePreview'
import InputField from '../AddressInput/InputField'
import styles from './styles.module.css'
import CtaV2 from '../../../../CtaV2'
import { $red60 } from '../../../../../styles/colors/primitives'
import { findItemById } from '../../../../../utils/shopItems'
import { PREVIEW_FAILURE_SLOT, affectedOfferIds } from './previewFailure'

// The order summary BEFORE an order exists: line items, discount, total, priced by
// the backend from the live cart. Presentational — the quote itself comes from
// useInvoicePreviewQuote, which PaymentPage runs ONCE and passes to both of its
// summary slots (mobile + desktop), so a cart change costs one backend call, not two.
//
// Error routing (see ./previewFailure.js for the classification):
//   - region    → the affected cart line(s) are named here AND PaymentPage shows
//                 the same failure under the shipping-country picker
//   - stock     → the affected cart line, with the backend's own shortfall sentence
//   - cart      → the summary card, about the cart as a whole
//   - transport → the summary card, with a Try again (these are ours, retryable)
// The discount-code slot keeps exactly ONE source: `discountWarning` on a
// SUCCESSFUL quote — the only failure that is actually about the promo code.

/**
 * @param {Object} props
 * @param {import('./useInvoicePreviewQuote').InvoicePreviewQuote} props.quote
 * @param {string|null} props.discountCode      the code the quote was priced with
 * @param {(code: string) => void} props.onDiscountCodeSubmit
 * @param {boolean} props.showDiscountCodeOption
 */
function InvoicePreviewWithoutOrder ({ quote, discountCode, onDiscountCodeSubmit, showDiscountCodeOption }) {
  const { offers, offersLoading } = useRegionOffers()
  const newRegion = useRegionCountryCode()
  const shoppingCart = useCartStore(selectCart)
  const openCart = useUiStore((s) => s.openCart)

  const { invoice, isLoading, failure, discountWarning, retry } = quote

  // The discount input opens on demand, and on its own when the code was refused
  // (so the reason has somewhere to show). A fresh successful quote closes it —
  // the same "collapse after Apply" the component always had.
  const [discountInputOpen, setDiscountInputOpen] = useState(false)
  useEffect(() => {
    if (invoice && !discountWarning) setDiscountInputOpen(false)
  }, [invoice, discountWarning])
  const showDiscountCodeInput = discountInputOpen || !!discountWarning

  if (!offers || !offers.length || offersLoading || isLoading) {
    // offers not loaded yet, or a quote is in flight
    return (
      <div className={invoiceStyles.invoiceWrapper}>
        <Text type="heading-8">SUMMARY</Text>
        <div className={invoiceStyles.loading}>
          <CircularProgress color="inherit"/>
        </div>
      </div>
    )
  }

  if (invoice) {
    return renderInvoice(
      invoice,
      showDiscountCodeOption
        ? renderDiscountCodeInvoiceItem(
          () => setDiscountInputOpen(!discountInputOpen),
          showDiscountCodeInput,
          discountCode,
          onDiscountCodeSubmit,
          discountWarning
        )
        : null,
      offers,
      newRegion
    )
  }

  if (failure) {
    return renderFailure(failure, shoppingCart, offers, retry, openCart)
  }

  return null
}

/**
 * The summary card when the cart could not be priced. Keeps the same SUMMARY card
 * as a priced invoice so the checkout column never collapses to nothing.
 */
const renderFailure = (failure, shoppingCart, offers, retry, openCart) => {
  const affected = affectedOfferIds(failure, offers)
  const cartIds = Object.keys(shoppingCart || {})
  const lineLevel = failure.slot === PREVIEW_FAILURE_SLOT.REGION || failure.slot === PREVIEW_FAILURE_SLOT.STOCK
  // A stock shortfall inside a bundle matches no offer by primary type; the
  // backend's own sentence still names the item, so it is shown at cart level then.
  const showLines = lineLevel && cartIds.length > 0
  const showDetailAtCartLevel = lineLevel && affected.size === 0 && failure.detail

  return (
    <div className={invoiceStyles.invoiceWrapper} data-testid="invoice-preview-failure" data-failure-slot={failure.slot}>
      <Text type="heading-8">SUMMARY</Text>
      { showLines && cartIds.map((id) => {
        const offer = findItemById(id, offers)
        const isAffected = affected.has(id)
        return (
          <div
            className={`${invoiceStyles.invoiceItem} ${isAffected ? styles.cartLineAffected : ''}`}
            key={id}
            data-testid={isAffected ? 'invoice-preview-line-affected' : 'invoice-preview-line'}
          >
            <div className={invoiceStyles.itemTitle}>
              <Text type="small">{(offer && offer.name) || id}</Text>
              { isAffected && <Text type="tiny" color={$red60}>{failure.slot === PREVIEW_FAILURE_SLOT.STOCK && failure.detail ? failure.detail : failure.message}</Text> }
            </div>
            <div className={invoiceStyles.itemPrice}>
              <Text type="small">{`× ${shoppingCart[id]}`}</Text>
            </div>
          </div>
        )
      }) }
      <div className={`${styles.cartError} ${showLines ? invoiceStyles.invoiceItemBorder : ''}`} data-testid="invoice-preview-failure-message">
        { showDetailAtCartLevel && <Text type="small" color={$red60}>{failure.detail}</Text> }
        <Text type="small" color={$red60}>{failure.message}</Text>
      </div>
      <div className={styles.failureActions}>
        { failure.transient && (
          <CtaV2 title="Try again" dark onClick={retry} sx={{ titleTextType: 'small' }}/>
        ) }
        { (failure.slot === PREVIEW_FAILURE_SLOT.CART || failure.slot === PREVIEW_FAILURE_SLOT.STOCK || failure.slot === PREVIEW_FAILURE_SLOT.REGION) && (
          <CtaV2 title="Review cart" onClick={openCart} sx={{ titleTextType: 'small' }}/>
        ) }
      </div>
    </div>
  )
}

export const renderDiscountCodeInvoiceItem = (onTitleClick, showInput, discountCode, onSubmit, errorMessage) => {
  let discountCodeUpdated = discountCode
  return (
    <div
      className={`${invoiceStyles.invoiceItem} ${invoiceStyles.invoiceItemBorder}`}
      key='discount-code'
    >
      <div className={styles.discountCode}>
        <div className={styles.addDiscountCodeText}>
          <div className={invoiceStyles.itemTitle} onClick={onTitleClick}>
            <Text type="small">ADD DISCOUNT CODE</Text>
          </div>
        </div>
        {
          showInput && <>
            <div className={styles.discountInputWrapper}>
              <InputField
                labelText={null}
                value={discountCodeUpdated}
                placeholder="Code"
                onChange={(value) => discountCodeUpdated = value}
              />
              <CtaV2 title="Apply" dark onClick={() => onSubmit(discountCodeUpdated)} sx={{ titleTextType: 'small' }}/>
            </div>
            { errorMessage && <Text type="small" color={$red60}>{errorMessage}</Text> }
          </>
        }
      </div>
    </div>
  )
}

export default InvoicePreviewWithoutOrder
